/**
 * MiniAppRenderer.js
 * Renders a Mini-App spec with fixed components. The spec is data only: text
 * goes through React's escaping, numbers through miniAppFormula, the accent,
 * the design and the CTA link are re-checked here. Nothing in a spec can add
 * markup, script or a link the sender did not set.
 *
 * Used by the public page (/miniapp/:publicId) and the Sales Studio editor.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { evaluateMiniApp } from "./miniAppFormula";
import { readableAccent, sanitizeBrand, sanitizeDesign } from "./miniAppDesign";
import "./MiniApp.css";

const LOCALES = { de: "de-DE", en: "en-US" };

const COPY = {
  de: {
    for: (b) => `Erstellt für ${b}`,
    inputs: "Ihre Werte",
    scenario: "Szenario",
    from: (s) => `Von ${s}`,
    status: { ok: "Passt", open: "Offen" },
    live: "Live",
    perDay: (v) => `≈ ${v} pro Tag`,
    since: (d) => `Seit ${d}`,
    sinceOpen: (v) => `seit Sie diese Seite geöffnet haben: +${v}`,
    month: (m) => `Monat ${m}`,
    after: (m) => `Nach ${m} Monaten`,
    breakEven: (m) => `Break-even in Monat ${m}`,
    today: "Heute",
  },
  en: {
    for: (b) => `Built for ${b}`,
    inputs: "Your numbers",
    scenario: "Scenario",
    from: (s) => `From ${s}`,
    status: { ok: "Good", open: "Open" },
    live: "Live",
    perDay: (v) => `≈ ${v} per day`,
    since: (d) => `Since ${d}`,
    sinceOpen: (v) => `since you opened this page: +${v}`,
    month: (m) => `Month ${m}`,
    after: (m) => `After ${m} months`,
    breakEven: (m) => `Pays off in month ${m}`,
    today: "Today",
  },
};

/** Same rules as functions/helpers/miniAppPreview.js formatValue. */
export const formatMiniAppValue = (
  value,
  { format = "number", currency = "EUR", decimals = 0, suffix = "" } = {},
  language = "de",
) => {
  if (value == null || !Number.isFinite(value)) return "–";
  const locale = LOCALES[language] || "de-DE";
  const digits = {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  };
  let out;
  if (format === "currency") {
    try {
      out = new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        ...digits,
      }).format(value);
    } catch (_) {
      out = `${new Intl.NumberFormat(locale, digits).format(value)} ${currency}`;
    }
  } else if (format === "percent") {
    out = `${new Intl.NumberFormat(locale, digits).format(value)} %`;
  } else {
    out = new Intl.NumberFormat(locale, digits).format(value);
  }
  return suffix ? `${out} ${suffix}` : out;
};

const safeHref = (value) => {
  const s = String(value || "");
  return /^https:\/\//i.test(s) || /^mailto:[^\s<>"']+@[^\s<>"']+$/i.test(s)
    ? s
    : "";
};
const list = (value) =>
  Array.isArray(value)
    ? value
    : value && typeof value === "object"
      ? Object.values(value)
      : [];

const SEGMENT_MAX_OPTIONS = 4;
const SEGMENT_MAX_CHARS = 34;
const SECONDS = { year: 365 * 86400, month: 30.44 * 86400 };

const reducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** Eases from the last shown value to `target`, starting from 0 on mount. */
const useCountUp = (target, duration = 900) => {
  const [shown, setShown] = useState(() => (reducedMotion() ? target : 0));
  const current = useRef(reducedMotion() ? target : 0);
  useEffect(() => {
    if (target == null || !Number.isFinite(target) || reducedMotion()) {
      current.current = target;
      setShown(target);
      return undefined;
    }
    const from = Number.isFinite(current.current) ? current.current : 0;
    const start = performance.now();
    let frame;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const v = from + (target - from) * (1 - (1 - t) ** 4);
      current.current = v;
      setShown(t < 1 ? v : target);
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, duration]);
  return shown;
};

/** Adds `is-in` once the element scrolls into view (always on without IntersectionObserver). */
const Reveal = ({ as: Tag = "section", className = "", children, ...rest }) => {
  const node = useRef(null);
  const [inView, setInView] = useState(
    () => typeof IntersectionObserver === "undefined" || reducedMotion(),
  );
  useEffect(() => {
    if (inView || !node.current) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    observer.observe(node.current);
    return () => observer.disconnect();
  }, [inView]);
  return (
    <Tag
      ref={node}
      className={`ma-reveal ${inView ? "is-in" : ""} ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
};

const Value = ({ value, spec, language }) => {
  const shown = useCountUp(value);
  return (
    <>
      {formatMiniAppValue(
        value == null ? value : shown,
        { ...spec, suffix: "" },
        language,
      )}
      {spec.suffix && value != null && (
        <span className="ma-suffix">{spec.suffix}</span>
      )}
    </>
  );
};

const Field = ({ input, value, onChange, language }) => {
  const locale = LOCALES[language] || "de-DE";
  const setNumber = (raw) => {
    const n = Number(String(raw).replace(",", "."));
    onChange(Number.isFinite(n) ? n : input.default);
  };
  const shown = `${new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value)}${input.unit ? ` ${input.unit}` : ""}`;
  const options = list(input.options);
  const segmented =
    input.type === "select" &&
    options.length <= SEGMENT_MAX_OPTIONS &&
    options.reduce((n, o) => n + String(o.label).length, 0) <=
      SEGMENT_MAX_CHARS;

  const head = (extra) => (
    <div className="ma-fieldHead">
      <span className="ma-fieldLabel">{input.label}</span>
      {extra}
    </div>
  );
  const help = input.help ? <p className="ma-help">{input.help}</p> : null;

  if (input.type === "toggle") {
    return (
      <div className="ma-field">
        {head(
          <button
            type="button"
            role="switch"
            aria-checked={value === 1}
            aria-label={input.label}
            className={`ma-switch ${value === 1 ? "on" : ""}`}
            onClick={() => onChange(value === 1 ? 0 : 1)}
          >
            <span />
          </button>,
        )}
        {help}
      </div>
    );
  }

  if (input.type === "select") {
    return (
      <div className="ma-field">
        {head(null)}
        {segmented ? (
          <div
            className="ma-segment"
            role="radiogroup"
            aria-label={input.label}
          >
            {options.map((o) => (
              <button
                type="button"
                key={`${o.value}-${o.label}`}
                role="radio"
                aria-checked={value === o.value}
                className={value === o.value ? "on" : ""}
                onClick={() => onChange(o.value)}
              >
                {o.label}
              </button>
            ))}
          </div>
        ) : (
          <select
            className="ma-select"
            value={value}
            onChange={(e) => setNumber(e.target.value)}
            aria-label={input.label}
          >
            {options.map((o) => (
              <option key={`${o.value}-${o.label}`} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        )}
        {help}
      </div>
    );
  }

  if (input.type === "slider") {
    const min = Number(input.min);
    const max = Number(input.max);
    const fill =
      max > min
        ? Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100))
        : 0;
    return (
      <div className="ma-field">
        {head(<span className="ma-fieldValue">{shown}</span>)}
        <input
          className="ma-range"
          type="range"
          min={input.min}
          max={input.max}
          step={input.step || "any"}
          value={value}
          style={{ "--ma-fill": `${fill}%` }}
          onChange={(e) => setNumber(e.target.value)}
          aria-label={input.label}
        />
        {help}
      </div>
    );
  }

  return (
    <div className="ma-field">
      {head(null)}
      <label className="ma-number">
        <input
          type="number"
          inputMode="decimal"
          value={value}
          min={input.min}
          max={input.max}
          step={input.step || "any"}
          onChange={(e) => setNumber(e.target.value)}
          aria-label={input.label}
        />
        {input.unit && <span>{input.unit}</span>}
      </label>
      {help}
    </div>
  );
};

/** Money-counter look: the digits after the second decimal run small and fast. */
const TickerNumber = ({ value, output, language }) => {
  const locale = LOCALES[language] || "de-DE";
  const options = { minimumFractionDigits: 4, maximumFractionDigits: 4 };
  let parts;
  try {
    parts =
      output.format === "currency"
        ? new Intl.NumberFormat(locale, {
            style: "currency",
            currency: output.currency || "EUR",
            ...options,
          }).formatToParts(value)
        : new Intl.NumberFormat(locale, options).formatToParts(value);
  } catch (_) {
    parts = new Intl.NumberFormat(locale, options).formatToParts(value);
  }
  return parts.map((p, i) =>
    p.type === "fraction" ? (
      <React.Fragment key={i}>
        {p.value.slice(0, 2)}
        <span className="ma-tickerFine">{p.value.slice(2)}</span>
      </React.Fragment>
    ) : (
      <React.Fragment key={i}>{p.value}</React.Fragment>
    ),
  );
};

const periodStart = (per, now) =>
  per === "month"
    ? new Date(now.getFullYear(), now.getMonth(), 1).getTime()
    : new Date(now.getFullYear(), 0, 1).getTime();

/**
 * Live counter: what a yearly / monthly amount has added up to since the
 * start of this year (or month), plus what came on top since the page opened.
 */
const Ticker = ({ ticker, value, output, language, copy }) => {
  const [now, setNow] = useState(() => Date.now());
  const opened = useRef(now);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 80);
    return () => clearInterval(id);
  }, []);
  if (value == null || !Number.isFinite(value)) return null;
  const perSecond = Math.abs(value) / SECONDS[ticker.per];
  const since = periodStart(ticker.per, new Date(now));
  const total = (perSecond * (now - since)) / 1000;
  const sinceOpen = (perSecond * (now - opened.current)) / 1000;
  const fmt = (v, decimals) =>
    formatMiniAppValue(v, { ...output, decimals, suffix: "" }, language);
  const daily = perSecond * 86400;
  const startLabel = new Intl.DateTimeFormat(LOCALES[language] || "de-DE", {
    day: "numeric",
    month: "long",
  }).format(since);
  return (
    <Reveal className="ma-ticker">
      <div className="ma-tickerHead">
        <span className="ma-live">
          <i aria-hidden="true" />
          {copy.live}
        </span>
        <p>{ticker.label}</p>
      </div>
      <div className="ma-tickerMain">
        <span className="ma-tickerSince">{copy.since(startLabel)}</span>
        <strong className="ma-tickerValue">
          <TickerNumber value={total} output={output} language={language} />
        </strong>
      </div>
      <span className="ma-tickerRate">
        {copy.perDay(fmt(daily, daily < 100 ? 2 : 0))} ·{" "}
        {copy.sinceOpen(fmt(sinceOpen, 2))}
      </span>
    </Reveal>
  );
};

const CHART_PAD = { top: 18, bottom: 18 };

/** Pixel size of an element, so SVG strokes are drawn without scaling. */
const useSize = (fallback) => {
  const node = useRef(null);
  const [size, setSize] = useState(fallback);
  useEffect(() => {
    if (!node.current || typeof ResizeObserver === "undefined")
      return undefined;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width && height)
        setSize({ w: Math.round(width), h: Math.round(height) });
    });
    observer.observe(node.current);
    return () => observer.disconnect();
  }, []);
  return [node, size];
};

const Projection = ({
  projection,
  monthly,
  upfront,
  output,
  language,
  copy,
}) => {
  const [hover, setHover] = useState(null);
  const [chartRef, { w: CHART_W, h: CHART_H }] = useSize({ w: 640, h: 220 });
  if (monthly == null || !Number.isFinite(monthly)) return null;
  const months = projection.months;
  const cost = Number.isFinite(upfront) ? Math.abs(upfront) : 0;
  const points = Array.from(
    { length: months + 1 },
    (_, m) => monthly * m - cost,
  );
  const lo = Math.min(0, ...points);
  const hi = Math.max(0, ...points);
  const span = hi - lo || 1;
  const x = (m) => (m / months) * CHART_W;
  const y = (v) =>
    CHART_PAD.top +
    ((hi - v) / span) * (CHART_H - CHART_PAD.top - CHART_PAD.bottom);
  const line = points
    .map((v, m) => `${m ? "L" : "M"}${x(m).toFixed(1)},${y(v).toFixed(1)}`)
    .join(" ");
  const zero = y(0);
  const area = `${line} L${CHART_W},${zero.toFixed(1)} L0,${zero.toFixed(1)} Z`;
  const breakEven = cost > 0 && monthly > 0 ? Math.ceil(cost / monthly) : null;
  const fmt = (v) => formatMiniAppValue(v, { ...output, suffix: "" }, language);
  const end = points[months];
  const active = hover == null ? null : { m: hover, v: points[hover] };

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(
      1,
      Math.max(0, (e.clientX - rect.left) / rect.width),
    );
    setHover(Math.round(ratio * months));
  };

  return (
    <Reveal className="ma-panel ma-projection">
      <div className="ma-projHead">
        <div>
          {projection.title && <h2 className="ma-h2">{projection.title}</h2>}
          <span className="ma-projLabel">
            {projection.label || copy.after(months)}
          </span>
          <strong className="ma-projValue">
            <Value
              value={end}
              spec={{ ...output, suffix: "" }}
              language={language}
            />
          </strong>
        </div>
        {breakEven != null && breakEven <= months && (
          <span className="ma-projBadge">
            <i className="fa-solid fa-flag-checkered" aria-hidden="true" />
            {copy.breakEven(breakEven)}
          </span>
        )}
      </div>
      <div
        className="ma-projChart"
        ref={chartRef}
        onPointerMove={onMove}
        onPointerLeave={() => setHover(null)}
      >
        <svg
          viewBox={`0 0 ${CHART_W} ${CHART_H}`}
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="ma-proj-fill" x1="0" y1="0" x2="0" y2="1">
              <stop className="ma-projStop" offset="0" stopOpacity="0.3" />
              <stop className="ma-projStop" offset="1" stopOpacity="0" />
            </linearGradient>
          </defs>
          <line
            className="ma-projZero"
            x1="0"
            x2={CHART_W}
            y1={zero}
            y2={zero}
          />
          <path className="ma-projArea" d={area} fill="url(#ma-proj-fill)" />
          <path className="ma-projLine" d={line} pathLength="1" />
          {active && (
            <line
              className="ma-projCursor"
              x1={x(active.m)}
              x2={x(active.m)}
              y1="0"
              y2={CHART_H}
            />
          )}
        </svg>
        {breakEven != null && breakEven <= months && (
          <span
            className="ma-projDot ma-projDot--even"
            style={{
              left: `${(x(breakEven) / CHART_W) * 100}%`,
              top: `${(y(points[breakEven]) / CHART_H) * 100}%`,
            }}
          />
        )}
        <span
          className="ma-projDot ma-projDot--end"
          style={{ left: "100%", top: `${(y(end) / CHART_H) * 100}%` }}
        />
        {active && (
          <>
            <span
              className="ma-projDot"
              style={{
                left: `${(x(active.m) / CHART_W) * 100}%`,
                top: `${(y(active.v) / CHART_H) * 100}%`,
              }}
            />
            <span
              className={`ma-projTip ${active.m > months * 0.7 ? "ma-projTip--left" : ""}`}
              style={{ left: `${(x(active.m) / CHART_W) * 100}%` }}
            >
              <small>{active.m ? copy.month(active.m) : copy.today}</small>
              {fmt(active.v)}
            </span>
          </>
        )}
      </div>
      <div className="ma-projAxis" aria-hidden="true">
        <span>{copy.today}</span>
        <span>{copy.month(Math.round(months / 2))}</span>
        <span>{copy.month(months)}</span>
      </div>
    </Reveal>
  );
};

const EDITORIAL_FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Newsreader:opsz,wght@6..72,400;6..72,500&display=swap";

const useEditorialFont = (active) => {
  useEffect(() => {
    if (
      !active ||
      document.querySelector(`link[href="${EDITORIAL_FONT_HREF}"]`)
    )
      return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = EDITORIAL_FONT_HREF;
    document.head.appendChild(link);
  }, [active]);
};

const MiniAppRenderer = ({ spec }) => {
  useEditorialFont(sanitizeDesign(spec?.design).font === "editorial");
  const language = COPY[spec?.language] ? spec.language : "de";
  const copy = COPY[language];
  const inputs = list(spec?.inputs);
  const outputs = list(spec?.outputs);
  const [values, setValues] = useState(() =>
    Object.fromEntries(inputs.map((i) => [i.id, Number(i.default) || 0])),
  );

  const result = useMemo(
    () =>
      evaluateMiniApp(
        {
          ...spec,
          inputs,
          outputs,
          chart: spec?.chart
            ? { ...spec.chart, bars: list(spec.chart.bars) }
            : null,
        },
        values,
      ),
    [spec, inputs, outputs, values],
  );

  if (!spec) return null;
  const design = sanitizeDesign(spec.design);
  const hidden = new Set(design.hide);
  const accent = design.accent || readableAccent(spec.accent);
  const highlight = outputs.find((o) => o.highlight) || outputs[0];
  const rest = outputs.filter((o) => o !== highlight);
  const outputById = (id) => outputs.find((o) => o.id === id);
  const bars = spec.chart && !hidden.has("chart") ? list(spec.chart.bars) : [];
  const barMax = Math.max(1, ...result.bars.map((v) => Math.abs(v || 0)));
  const comparison = !hidden.has("comparison") ? spec.comparison : null;
  const checklist = !hidden.has("checklist") ? spec.checklist : null;
  const scenarios = hidden.has("scenarios") ? [] : list(spec.scenarios);
  const ticker =
    !hidden.has("ticker") && spec.ticker && outputById(spec.ticker.output)
      ? spec.ticker
      : null;
  const projection =
    !hidden.has("projection") &&
    spec.projection &&
    outputById(spec.projection.monthly)
      ? spec.projection
      : null;
  const insight = hidden.has("insight") ? "" : spec.insight;
  const cta = spec.cta;
  const ctaHref = cta ? safeHref(cta.url) : "";
  const sender = hidden.has("sender")
    ? ""
    : [spec.sender?.name, spec.sender?.company].filter(Boolean).join(", ");
  const footnote = hidden.has("footnote") ? "" : spec.footnote;
  const brand = design.brand !== "off" ? sanitizeBrand(spec.brand) : null;
  const logoUrl = brand ? brand.logoUrl : "";
  const columns = comparison ? list(comparison.columns) : [];
  const items = checklist ? list(checklist.items) : [];
  const scenarioOn = (s) =>
    Object.entries(s.values || {}).every(([id, v]) => values[id] === v);

  const className = [
    "ma",
    `ma--${design.theme}`,
    `ma--${design.font}`,
    `ma--${design.corners}`,
    `ma--${design.layout}`,
  ].join(" ");

  return (
    <div
      className={className}
      style={{ "--ma-accent": accent }}
      lang={language}
    >
      <div className="ma-aurora" aria-hidden="true" />
      <header className="ma-head">
        {(spec.business || logoUrl) && (
          <div className="ma-headTop">
            {spec.business && (
              <span className="ma-kicker">
                <i aria-hidden="true" />
                {copy.for(spec.business)}
              </span>
            )}
            {logoUrl && (
              <span
                className={`ma-logo ${brand.logoOnDark ? "ma-logo--plate" : ""}`}
              >
                <img
                  src={logoUrl}
                  alt={spec.business || ""}
                  referrerPolicy="no-referrer"
                />
              </span>
            )}
          </div>
        )}
        <h1 className="ma-title">{spec.title}</h1>
        {spec.subtitle && <p className="ma-lead">{spec.subtitle}</p>}
        {spec.intro && <p className="ma-intro">{spec.intro}</p>}
      </header>

      {insight && (
        <Reveal as="blockquote" className="ma-insight">
          {insight}
        </Reveal>
      )}

      {scenarios.length > 1 && inputs.length > 0 && (
        <div className="ma-scenarios">
          <span>{copy.scenario}</span>
          <div
            className="ma-segment"
            role="radiogroup"
            aria-label={copy.scenario}
          >
            {scenarios.map((s, i) => (
              <button
                type="button"
                key={`${s.label}-${i}`}
                role="radio"
                aria-checked={scenarioOn(s)}
                className={scenarioOn(s) ? "on" : ""}
                onClick={() => setValues((prev) => ({ ...prev, ...s.values }))}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {(inputs.length > 0 || outputs.length > 0) && (
        <section
          className={`ma-calc ${inputs.length && outputs.length ? "" : "ma-calc--single"}`}
        >
          {outputs.length > 0 && (
            <div className="ma-results">
              {highlight && (
                <div className="ma-hero">
                  <span className="ma-heroLabel">{highlight.label}</span>
                  <strong className="ma-heroValue">
                    <Value
                      value={result.outputs[highlight.id]}
                      spec={highlight}
                      language={language}
                    />
                  </strong>
                  {highlight.note && (
                    <p className="ma-heroNote">{highlight.note}</p>
                  )}
                </div>
              )}
              {rest.length > 0 && (
                <dl className="ma-stats">
                  {rest.map((o) => (
                    <div className="ma-stat" key={o.id}>
                      <dt>
                        {o.label}
                        {o.note && <small>{o.note}</small>}
                      </dt>
                      <dd>
                        <Value
                          value={result.outputs[o.id]}
                          spec={o}
                          language={language}
                        />
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          )}
          {inputs.length > 0 && (
            <div className="ma-panel ma-inputs">
              <h2 className="ma-h2">{copy.inputs}</h2>
              {inputs.map((input) => (
                <Field
                  key={input.id}
                  input={input}
                  value={values[input.id]}
                  onChange={(v) =>
                    setValues((prev) => ({ ...prev, [input.id]: v }))
                  }
                  language={language}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {ticker && (
        <Ticker
          ticker={ticker}
          value={result.outputs[ticker.output]}
          output={outputById(ticker.output)}
          language={language}
          copy={copy}
        />
      )}

      {projection && (
        <Projection
          projection={projection}
          monthly={result.outputs[projection.monthly]}
          upfront={projection.upfront ? result.outputs[projection.upfront] : 0}
          output={outputById(projection.monthly)}
          language={language}
          copy={copy}
        />
      )}

      {bars.length > 1 && (
        <Reveal className="ma-panel ma-chart">
          {spec.chart.title && <h2 className="ma-h2">{spec.chart.title}</h2>}
          {bars.map((bar, i) => (
            <div
              className={`ma-bar ${i === bars.length - 1 ? "ma-bar--key" : ""}`}
              key={`${bar.label}-${i}`}
              style={{ "--i": i }}
            >
              <div className="ma-barHead">
                <span>{bar.label}</span>
                <strong>
                  <Value
                    value={result.bars[i]}
                    spec={spec.chart}
                    language={language}
                  />
                </strong>
              </div>
              <div className="ma-barTrack">
                <div
                  style={{
                    "--w": `${Math.max(1.5, (Math.abs(result.bars[i] || 0) / barMax) * 100)}%`,
                  }}
                />
              </div>
            </div>
          ))}
        </Reveal>
      )}

      {columns.length > 1 && (
        <Reveal className="ma-panel ma-compare">
          {comparison.title && <h2 className="ma-h2">{comparison.title}</h2>}
          <div className="ma-tableWrap">
            <table>
              <thead>
                <tr>
                  <th aria-hidden="true" />
                  {columns.map((c, i) => (
                    <th
                      key={`${c}-${i}`}
                      className={i === comparison.highlightColumn ? "hl" : ""}
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list(comparison.rows).map((row, r) => (
                  <tr key={`${row.label}-${r}`}>
                    <th scope="row">{row.label}</th>
                    {columns.map((_, i) => (
                      <td
                        key={i}
                        className={i === comparison.highlightColumn ? "hl" : ""}
                      >
                        {list(row.values)[i] || ""}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      )}

      {items.length > 0 && (
        <Reveal className="ma-panel ma-check">
          {checklist.title && <h2 className="ma-h2">{checklist.title}</h2>}
          <ul>
            {items.map((item, i) => (
              <li
                key={`${item.label}-${i}`}
                className={item.ok ? "ok" : "open"}
                style={{ "--i": i }}
              >
                <span
                  className="ma-checkMark"
                  aria-label={item.ok ? copy.status.ok : copy.status.open}
                >
                  <i
                    className={`fa-solid ${item.ok ? "fa-check" : "fa-minus"}`}
                    aria-hidden="true"
                  />
                </span>
                <div>
                  <strong>{item.label}</strong>
                  {item.note && <span>{item.note}</span>}
                </div>
              </li>
            ))}
          </ul>
        </Reveal>
      )}

      {cta && (cta.label || cta.text) && (
        <Reveal className="ma-cta">
          {cta.text && <p>{cta.text}</p>}
          {ctaHref && cta.label && (
            <a
              className="ma-ctaBtn"
              href={ctaHref}
              target="_blank"
              rel="noopener noreferrer nofollow"
            >
              {cta.label}
              <i className="fa-solid fa-arrow-right" aria-hidden="true" />
            </a>
          )}
        </Reveal>
      )}

      {(footnote || sender) && (
        <footer className="ma-foot">
          {footnote && <p>{footnote}</p>}
          {sender && <p className="ma-sender">{copy.from(sender)}</p>}
        </footer>
      )}
    </div>
  );
};

export default MiniAppRenderer;
